import { AxiosError } from 'axios';
import axiosInstance from '../utils/axios';

/**
 * Checkout against the stand-in processor.
 *
 * No money moves. The real grant path is the verified payment webhook of
 * MONETIZATION_FOUNDATION §7, which is blocked on the §12 pricing and processor
 * decisions; this exists so the paywall can be exercised end to end in the meantime.
 *
 * `available: false` is the normal answer on any deployment without the dummy processor
 * switched on, and it is not an error — the caller is expected to explain rather than
 * show a broken checkout.
 */

export interface CheckoutSku {
  sku: string;
  name: string;
  description: string | null;
  amountPaise: number;
  currency: string;
  /** Null for a one-time purchase that never expires. */
  validDays: number | null;
}

export interface DemoInstrument {
  id: string;
  type: string;
  brand: string | null;
  funding: string | null;
  last4: string | null;
  label: string;
  /** True for the instruments that always decline — used to exercise the failure path. */
  willDecline: boolean;
}

export interface CheckoutOffer {
  available: boolean;
  demo: boolean;
  processor: string;
  notice: string;
  skus: CheckoutSku[];
  instruments: DemoInstrument[];
  acceptsRawCards: boolean;
}

export interface PaymentOrder {
  orderId: string;
  sku: string;
  amountPaise: number;
  discountPaise: number;
  payablePaise: number;
  currency: string;
  status: 'CREATED' | 'PAID' | 'FAILED';
  couponCode: string | null;
  instrumentLabel: string | null;
  failureReason: string | null;
  reference: string | null;
  demo: boolean;
  paidAt: string | null;
}

export type OrderResult =
  | { state: 'ok'; order: PaymentOrder }
  /** No processor on this deployment — a normal state, not a failure. */
  | { state: 'unavailable'; message: string }
  | { state: 'error'; message: string };

/** Paise are integers on the wire; money is never a float. */
export const formatPaise = (paise: number): string =>
  `₹${(paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

export const fetchCheckoutOffer = async (): Promise<CheckoutOffer | null> => {
  try {
    const response = await axiosInstance.get('/checkout/offer');
    if (response.data?.status === 'success' && response.data?.data) {
      return response.data.data as CheckoutOffer;
    }
    return null;
  } catch {
    return null;
  }
};

export const createOrder = async (
  sku: string,
  couponCode?: string
): Promise<OrderResult> => {
  try {
    const response = await axiosInstance.post('/checkout/order', { sku, couponCode });
    if (response.data?.status === 'success' && response.data?.data) {
      return { state: 'ok', order: response.data.data as PaymentOrder };
    }
    return { state: 'error', message: response.data?.message ?? 'Unexpected response' };
  } catch (err) {
    const error = err as AxiosError<{ message?: string }>;
    if (error?.response?.status === 503) {
      return {
        state: 'unavailable',
        message:
          error.response?.data?.message ??
          'No payment processor is configured on this deployment.'
      };
    }
    return {
      state: 'error',
      message: error?.response?.data?.message ?? 'Could not start the checkout.'
    };
  }
};

/**
 * A declined card returns `ok` with `order.status === 'FAILED'` — the request succeeded
 * and the answer was no. Only a request that could not be handled at all is an error.
 */
export const payOrder = async (
  orderId: string,
  instrumentId: string
): Promise<OrderResult> => {
  try {
    const response = await axiosInstance.post(`/checkout/order/${orderId}/pay`, {
      instrumentId
    });
    if (response.data?.status === 'success' && response.data?.data) {
      return { state: 'ok', order: response.data.data as PaymentOrder };
    }
    return { state: 'error', message: response.data?.message ?? 'Unexpected response' };
  } catch (err) {
    const error = err as AxiosError<{ message?: string }>;
    if (error?.response?.status === 503) {
      return {
        state: 'unavailable',
        message: error.response?.data?.message ?? 'No payment processor is configured.'
      };
    }
    return {
      state: 'error',
      message: error?.response?.data?.message ?? 'Could not complete the payment.'
    };
  }
};
